import { ChangeDetectorRef, Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { Store } from '@ngrx/store'

import * as fromSidebarStore from '@app/sidebar/store'
import * as fromUserStore from '@app/users/store'
import * as fromCoreStore from '@app/core/store'
import { USER_STATES } from '@app/shared/models/user.model'
import { AutoAssigneeUser } from '@app/sidebar/store/selectors/auto-assignee.selectors'
import { popupService } from '@app/shared/services/popup.service'
import { ToastsError } from '@app/shared/store'

@Component({
  selector: 'app-sidebar-auto-assignees',
  templateUrl: './sidebar-auto-assignees.component.html',
  styleUrls: ['./sidebar-auto-assignees.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SidebarAutoAssigneesComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<boolean>()

  USER_STATES = USER_STATES
  loaded = false
  users: AutoAssigneeUser[] = []
  activeCount = 0
  pausedCount = 0
  currentUserId: string | null = null

  scheduleOpen = false
  tempSchedule: { statusChangeStartDate: string | null, statusChangeEndDate: string | null } = {
    statusChangeStartDate: null,
    statusChangeEndDate: null
  }

  constructor(
    private store: Store<fromSidebarStore.State>,
    private cdr: ChangeDetectorRef,
    private _popupService: popupService
  ) {}

  isMe = (u: AutoAssigneeUser): boolean => !!this.currentUserId && u._id === this.currentUserId

  get me(): AutoAssigneeUser | undefined {
    return this.users.find(u => this.isMe(u))
  }

  ngOnInit(): void {
    this.store.dispatch(new fromSidebarStore.LoadAutoAssignees())

    this.store
      .select(fromUserStore.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(id => {
        this.currentUserId = id || null
        this.cdr.markForCheck()
      })

    this.store
      .select(fromSidebarStore.getAutoAssigneeLoaded)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(loaded => {
        this.loaded = loaded
        this.cdr.markForCheck()
      })

    this.store
      .select(fromSidebarStore.selectAutoAssigneeUsers)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(users => {
        this.users = users
        this.activeCount = users.filter(u => u._autoAssignee.active).length
        this.pausedCount = users.length - this.activeCount
        this.cdr.markForCheck()
      })
  }

  // Mirrors quote-list.toggleAutoAssign — clicking the pill toggles between active/paused.
  // When disabling (manual or scheduled), prompts for a reason + skipPreviousCustomers checkbox.
  toggleSelfActive() {
    const me = this.me
    if (!me) return

    if (me._autoAssignee.active) {
      // Manual disable — prompt for reason and checkbox, then disable immediately
      this._popupService.prompt(
        'Please provide a reason for the pause. Example: Vacation, Sick, Break, Catching Up, Other',
        {
          showCheckbox: true,
          checkboxLabel: "Don't Assign My Previous Customers To Me When I'm inactive",
          checkboxValue: false
        }
      ).then((res: string | { text: string, checked: boolean }) => {
        if (!res) return
        const reason = typeof res === 'string' ? res : res.text
        if (!reason) return
        const skipPrevious = typeof res === 'object' ? !!res.checked : false
        this.saveSelf({
          active: false,
          lastReasonPause: reason,
          statusChangeStartDate: '',
          statusChangeEndDate: '',
          skipPreviousCustomersWhenInactive: skipPrevious
        })
      })
    } else {
      // Manual enable — clear schedule + reason + skip flag
      this.saveSelf({
        active: true,
        lastReasonPause: '',
        statusChangeStartDate: '',
        statusChangeEndDate: '',
        skipPreviousCustomersWhenInactive: false
      })
    }
  }

  openSchedule() {
    const me = this.me
    if (!me) return
    this.tempSchedule = {
      statusChangeStartDate: me._autoAssignee.statusChangeStartDate || null,
      statusChangeEndDate: me._autoAssignee.statusChangeEndDate || null
    }
    this.scheduleOpen = true
  }

  closeSchedule() {
    this.scheduleOpen = false
    this.tempSchedule = { statusChangeStartDate: null, statusChangeEndDate: null }
  }

  // Mirrors quote-list scheduleAutoAssignDate / toggleAutoAssign with isScheduledDisable=true.
  // Active user setting future dates → keep active=true, prompt for reason + checkbox, the cron will flip later.
  // Paused user setting an end date → keep active=false, just save the date.
  saveSchedule() {
    const me = this.me
    if (!me) return

    const start = this.tempSchedule.statusChangeStartDate
    const end = this.tempSchedule.statusChangeEndDate

    if (start && end && new Date(start) >= new Date(end)) {
      this._popupService.alert('Turn off date must be before turn on date')
      return
    }

    if (!start && !end) {
      // Same as clearScheduledDates — keep active state, drop reason
      this.saveSelf({
        active: me._autoAssignee.active,
        lastReasonPause: '',
        statusChangeStartDate: '',
        statusChangeEndDate: ''
      })
      this.closeSchedule()
      return
    }

    if (me._autoAssignee.active) {
      // Scheduled disable — prompt for reason + skip checkbox, keep active=true
      this._popupService.prompt(
        'Please provide a reason for the scheduled pause. Example: Vacation, Sick, Break, Catching Up, Other',
        {
          showCheckbox: true,
          checkboxLabel: "Don't Assign My Previous Customers To Me When I'm inactive",
          checkboxValue: false
        }
      ).then((res: string | { text: string, checked: boolean }) => {
        if (!res) {
          this.store.dispatch(new ToastsError('No reason provided. Auto assignee settings were not updated.'))
          return
        }
        const reason = typeof res === 'string' ? res : res.text
        if (!reason) return
        const skipPrevious = typeof res === 'object' ? !!res.checked : false
        this.saveSelf({
          active: true,
          lastReasonPause: reason,
          statusChangeStartDate: start || '',
          statusChangeEndDate: end || '',
          skipPreviousCustomersWhenInactive: skipPrevious
        })
        this.closeSchedule()
      })
    } else {
      // Paused user scheduling a resume — keep active=false, just save dates
      this.saveSelf({
        active: false,
        statusChangeStartDate: start || '',
        statusChangeEndDate: end || ''
      })
      this.closeSchedule()
    }
  }

  // Mirrors quote-list isClearingDates branch — clears both dates + reason, keeps current active state.
  clearSchedule() {
    const me = this.me
    if (!me) return
    this.saveSelf({
      active: me._autoAssignee.active,
      lastReasonPause: '',
      statusChangeStartDate: '',
      statusChangeEndDate: ''
    })
    this.closeSchedule()
  }

  private saveSelf(payload: {
    active?: boolean,
    lastReasonPause?: string,
    statusChangeStartDate?: string,
    statusChangeEndDate?: string,
    skipPreviousCustomersWhenInactive?: boolean
  }) {
    if (!this.currentUserId) return
    // Dispatch through the shared effect so currentUser._autoAssignee in the user store
    // also gets patched — that's what the quote-list filter button reads.
    this.store.dispatch(new fromCoreStore.UpdateAutoAssignee({
      contactId: this.currentUserId,
      ...payload
    }))
  }

  ngOnDestroy() {
    this.destroyed$.next(true)
    this.destroyed$.complete()
  }
}
